import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  FaSearch, 
  FaEdit, 
  FaTrash, 
  FaEye, 
  FaPhone, 
  FaEnvelope, 
  FaUserTie, 
  FaIdBadge, 
  FaCalendarAlt, 
  FaUserGraduate, 
  FaMapMarkerAlt, 
  FaChevronDown, 
  FaChevronUp, 
  FaChevronLeft,
  FaChevronRight,
  FaTimes, 
  FaCheck, 
  FaTimesCircle, 
  FaWhatsapp, 
  FaQrcode,
  FaUsers,
  FaEllipsisV,
  FaUserSlash,
  FaUserCheck
} from 'react-icons/fa';
import { QRCodeSVG } from 'qrcode.react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChartPie,
  faChartBar,
  faUsers as faUsersSolid,
  faListAlt,
  faTicketAlt,
  faUsers,
  faTools,
  faGraduationCap,
  faIdCard,
  faFlag
} from '@fortawesome/free-solid-svg-icons';

function MemberDatabase() {
  // State management
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [dropdownMember, setDropdownMember] = useState(null);
  const [viewingMember, setViewingMember] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);

  // Notification state
  const [notifications, setNotifications] = useState([]);

  // Notification system
  const showNotification = (message, type = 'info') => {
    const id = Date.now();
    const newNotification = { id, message, type };
    
    // Add new notification
    setNotifications(prev => [...prev, newNotification]);
    
    // Auto remove after 5 seconds
    setTimeout(() => {
      removeNotification(id);
    }, 5000);
  };

  // Remove notification by id
  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  };
  
  // Open member profile modal
  const openMemberProfile = (member) => {
    setViewingMember(member);
    setIsModalOpen(true);
  };
  
  // Close member profile modal
  const closeModal = () => {
    setIsModalOpen(false);
    setViewingMember(null);
  };

  // Function to open QR code modal
  const openQRCodeModal = (member, e) => {
    if (e) e.stopPropagation();
    setViewingMember(member);
    setShowQRModal(true);
  };

  // Close modals
  const closeModals = () => {
    setIsModalOpen(false);
    setShowQRModal(false);
    setViewingMember(null);
  };

  // Get user info from localStorage
  const user = JSON.parse(localStorage.getItem('user')) || {
    name: 'Unknown User',
    role: 'Member',
    avatar: null
  };

  // Fetch members data
  useEffect(() => {
    const fetchMembers = async () => {
      try {
        setIsLoading(true);
        const apiUrl = 'https://dev.sliitleo.org/Backend/member_QRapi.php';
        console.log('Fetching members from:', apiUrl);
        
        // First try with credentials
        let response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });
        
        // If unauthorized, try without credentials
        if (response.status === 401 || response.status === 0) {
          console.log('Trying without credentials...');
          response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          });
        }
        
        console.log('Response status:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Error response:', errorText);
          throw new Error(`HTTP error! Status: ${response.status}: ${errorText}`);
        }
        
        let responseData;
        try {
          responseData = await response.json();
          // console.log('Raw API response:', responseData);
        } catch (jsonError) {
          console.error('Error parsing JSON:', jsonError);
          throw new Error('Invalid JSON response from server');
        }
        
        // Check if response is an array or an object with members array
        const membersData = Array.isArray(responseData) 
          ? responseData 
          : (responseData.members || []);
          
        // console.log('Processed members data:', membersData);
        
        if (!membersData.length) {
          console.warn('No members data received or empty array');
          setError('No member data available');
          return;
        }
        
        // Map the data to our expected format
        const formattedMembers = membersData.map(member => ({
          id: member.SLIITLeoID || member.id || '',
          name: member.FullName || member.name || '',
          SLIITID: member.SLIITID || member.SLIITID || '',
          clubId: member.SLIITLeoID || member.clubId || '',
          faculty: member.Faculty || member.faculty || '',
          year: member.Year || member.year || '',
          semester: member.Semester || member.semester || '',
          status: member.LeoClubStatus || member.status || 'Active',
          myLCI: member.MyLCI || member.myLCI || 0,
          role: member.Role || member.role || 'Club Member',
          registered: member.RegDate || member.registered || '',
          email: member.Email || member.email || '',
          contactNo: member.ContactNo || member.contactNo || member.contact || '',
          dob: member.DOB || member.dob || '',
          address: member.Address || member.address || '',
          postal: member.PostalCode || member.postal || '',
          school: member.School || member.school || '',
          skills: member.Skills || member.skills || '',
          nic: member.NIC || member.nic || ''
        }));
        
        // console.log('Formatted members:', formattedMembers);
        setMembers(formattedMembers);
        setError(null);
      } catch (err) {
        console.error('Error fetching members:', err);
        setError(`Failed to load members: ${err.message}`);
        showNotification(`Error: ${err.message}`, 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchMembers();
  }, []);

  // Helper to calculate age from DOB string (YYYY-MM-DD or similar)
  const calculateAge = (dob) => {
    if (!dob) return 'N/A';
    
    try {
      const birthDate = new Date(dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      return age > 0 ? age + ' years' : 'N/A';
    } catch (e) {
      return 'N/A';
    }
  };

  // Filter members based on search
  const filteredMembers = members.filter(member => {
    // Ensure we have a member object and search term
    if (!member || !search) return true;
    
    const searchTerm = search.toLowerCase();
    const name = member.name || '';
    const SLIITID = member.SLIITID || '';
    const email = member.email || '';
    const contactNo = member.contactNo || '';
    
    return (
      name.toString().toLowerCase().includes(searchTerm) ||
      SLIITID.toString().toLowerCase().includes(searchTerm) ||
      email.toString().toLowerCase().includes(searchTerm) ||
      contactNo.toString().includes(search)
    );
  });

  // Pagination
  const pageCount = Math.ceil(filteredMembers.length / rowsPerPage);
  const paginatedMembers = filteredMembers.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Toggle member selection
  const toggleMember = (SLIITLeoID) => {
    setSelectedMember(selectedMember === SLIITLeoID ? null : SLIITLeoID);
  };

  // Toggle MYLCI status for a member
  const toggleMyLCI = async (SLIITLeoID, currentStatus) => {
    try {
      const response = await fetch('https://dev.sliitleo.org/Backend/update_member_myLCI.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `SLIITLeoID=${encodeURIComponent(SLIITLeoID)}&myLCI=${!currentStatus}`,
      });

      const data = await response.json();

      if (data.success) {
        setMembers(prevMembers => 
          prevMembers.map(member => 
            member.SLIITLeoID === SLIITLeoID 
              ? { ...member, myLCI: !currentStatus } 
              : member
          )
        );
        showNotification(
          `${data.message} MYLCI status ${!currentStatus ? 'added to' : 'removed from'} member.`,
          'success'
        );
      } else {
        showNotification(
          data.message || 'Failed to update MYLCI status. Please try again.',
          'error'
        );
      }
    } catch (error) {
      console.error('Error updating MYLCI status:', error);
      showNotification('Failed to update MYLCI status. Please try again.', 'error');
    }
  };

  // Toggle dropdown
  const toggleDropdown = (memberId, e) => {
    if (e) e.stopPropagation();
    setDropdownMember(dropdownMember === memberId ? null : memberId);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex bg-[#111827] text-white min-h-screen">
        <Sidebar />
        <Topbar user={user} />
        <main className="md:ml-64 ml-0 mt-14 w-full px-2 sm:px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-10">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        </main>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex bg-[#111827] text-white min-h-screen">
        <Sidebar />
        <Topbar user={user} />
        <main className="md:ml-64 ml-0 mt-14 w-full px-2 sm:px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-10">
          <div className="bg-red-900/50 border border-red-700 rounded-lg p-4 text-center">
            <p className="text-red-200">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-2 px-4 py-2 bg-red-700 hover:bg-red-600 rounded-md transition-colors"
            >
              Retry
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex bg-[#111827] text-white min-h-screen">
      <Sidebar />
      <Topbar user={user} />
      <main className="md:ml-64 ml-0 mt-14 w-full px-2 sm:px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-10">
        {/* Header */}
        <div className="backdrop-blur-md bg-gradient-to-r from-[#23272f]/80 to-[#181b20]/80 border border-gray-800 rounded-2xl p-3 sm:p-4 mb-6 flex items-center gap-3">
          <span className="bg-red-600 p-2 sm:p-3 rounded-full shadow-lg flex-shrink-0">
            <FaUsers className="text-white text-lg" />
          </span>
          <div className="min-w-0">
            <h1 className="text-base sm:text-lg font-bold tracking-wide text-white truncate">Member Database</h1>
            <p className="text-sm sm:text-base text-gray-400 truncate">Manage and view all members</p>
          </div>
        </div>

        <div className="mb-4 sm:mb-6">
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3">
            <Link to="/members" className="flex-1 min-w-[120px] sm:flex-none flex items-center justify-center gap-1 sm:gap-2 bg-blue-900/50 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm md:text-base font-medium sm:font-semibold text-white border border-blue-700 shadow hover:scale-105 transition focus:outline-none focus:ring-1 sm:focus:ring-2 focus:ring-red-400 group">
              <span className="text-red-400 text-sm sm:text-base"><FontAwesomeIcon icon={faUsers} /></span>
              <span className="truncate">Members</span>
            </Link>
            <Link to="/events" className="flex-1 min-w-[120px] sm:flex-none flex items-center justify-center gap-1 sm:gap-2 bg-[#23272f] px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm md:text-base font-medium sm:font-semibold text-white border border-gray-700 shadow hover:scale-105 transition focus:outline-none focus:ring-1 sm:focus:ring-2 focus:ring-orange-400 group">
              <span className="text-orange-400 text-sm sm:text-base"><FontAwesomeIcon icon={faChartPie} /></span>
              <span className="truncate">Events</span>
            </Link>
            <Link to="/scan-qr-code" className="flex-1 min-w-[120px] sm:flex-none flex items-center justify-center gap-1 sm:gap-2 bg-[#23272f] px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm md:text-base font-medium sm:font-semibold text-white border border-gray-700 shadow hover:scale-105 transition focus:outline-none focus:ring-1 sm:focus:ring-2 focus:ring-blue-400 group">
              <span className="text-blue-400 text-sm sm:text-base"><FontAwesomeIcon icon={faChartBar} /></span>
              <span className="truncate">Scan QR</span>
            </Link>
            <Link to="/attendance" className="flex-1 min-w-[120px] sm:flex-none flex items-center justify-center gap-1 sm:gap-2 bg-green-600 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm md:text-base font-medium sm:font-semibold text-white border border-green-700 shadow hover:scale-105 transition focus:outline-none focus:ring-1 sm:focus:ring-2 focus:ring-green-400 group">
              <span className="text-white text-sm sm:text-base"><FontAwesomeIcon icon={faListAlt} /></span>
              <span className="truncate">Attendance</span>
            </Link>
          </div>
        </div>

        {/* Search and Actions */}
        <div className="mb-6 flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search members..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-4 py-2 w-full bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Members Table */}
        <div className="bg-gray-900/50 border border-gray-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-800/50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Reg No
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Contact
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-900/50 divide-y divide-gray-800">
                {paginatedMembers.length > 0 ? (
                  paginatedMembers.map((member) => (
                    <tr 
                      key={member.id} 
                      className={`hover:bg-gray-800/50 transition-colors ${selectedMember === member.id ? 'bg-gray-800' : ''}`}
                      onClick={() => toggleMember(member.id)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-700 flex items-center justify-center text-lg font-semibold">
                            {member.name ? member.name.charAt(0).toUpperCase() : '?'}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white">{member.name}</div>
                            <div className="text-xs text-gray-400">{member.faculty}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-300">{member.SLIITID || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-300 truncate max-w-xs">{member.email || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-300">{member.contactNo || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          member.status === 'Active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {member.status || 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openMemberProfile(member);
                            }}
                            className="text-blue-400 hover:text-blue-300"
                            title="View Profile"
                          >
                            <FaEye className="h-5 w-5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openQRCodeModal(member, e);
                            }}
                            className="text-purple-400 hover:text-purple-300"
                            title="QR Code"
                          >
                            <FaQrcode className="h-5 w-5" />
                          </button>
                          <div className="relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleDropdown(member.id, e);
                              }}
                              className="text-gray-400 hover:text-white"
                              title="More options"
                            >
                              <FaEllipsisV className="h-5 w-5" />
                            </button>
                            {dropdownMember === member.id && (
                              <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-gray-800 ring-1 ring-black ring-opacity-5 z-10">
                                <div className="py-1">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleMyLCI(member.id, member.myLCI);
                                      setDropdownMember(null);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2"
                                  >
                                    {member.myLCI ? (
                                      <>
                                        <FaUserSlash className="text-red-400" />
                                        Remove from MYLCI
                                      </>
                                    ) : (
                                      <>
                                        <FaUserCheck className="text-green-400" />
                                        Add to MYLCI
                                      </>
                                    )}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-gray-400">
                      No members found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pageCount > 1 && (
            <div className="px-6 py-4 border-t border-gray-800 flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`relative inline-flex items-center px-4 py-2 border border-gray-700 text-sm font-medium rounded-md ${
                    page === 1 ? 'bg-gray-800 text-gray-500 cursor-not-allowed' : 'text-white bg-gray-700 hover:bg-gray-600'
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => Math.min(pageCount, p + 1))}
                  disabled={page === pageCount}
                  className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-700 text-sm font-medium rounded-md ${
                    page === pageCount ? 'bg-gray-800 text-gray-500 cursor-not-allowed' : 'text-white bg-gray-700 hover:bg-gray-600'
                  }`}
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-400">
                    Showing <span className="font-medium">{(page - 1) * rowsPerPage + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(page * rowsPerPage, filteredMembers.length)}
                    </span>{' '}
                    of <span className="font-medium">{filteredMembers.length}</span> members
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-700 bg-gray-800 text-sm font-medium ${
                        page === 1 ? 'text-gray-500 cursor-not-allowed' : 'text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      <span className="sr-only">Previous</span>
                      <FaChevronLeft className="h-5 w-5" />
                    </button>
                    {Array.from({ length: Math.min(5, pageCount) }, (_, i) => {
                      // Show pages around current page
                      let pageNum;
                      if (pageCount <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= pageCount - 2) {
                        pageNum = pageCount - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === pageNum
                              ? 'z-10 bg-blue-600 border-blue-700 text-white'
                              : 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setPage(p => Math.min(pageCount, p + 1))}
                      disabled={page === pageCount}
                      className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-700 bg-gray-800 text-sm font-medium ${
                        page === pageCount ? 'text-gray-500 cursor-not-allowed' : 'text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      <span className="sr-only">Next</span>
                      <FaChevronRight className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* QR Code Modal */}
        {showQRModal && viewingMember && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6 relative">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-white">Member QR Code</h3>
                <button 
                  onClick={closeModals}
                  className="text-gray-400 hover:text-white"
                >
                  <FaTimes className="text-xl" />
                </button>
              </div>
              <div className="flex flex-col items-center py-4">
                <div className="bg-white p-4 rounded-lg mb-4">
                  <QRCodeSVG 
                    value={JSON.stringify({
                      id: viewingMember.SLIITLeoID,
                      name: viewingMember.name,
                      SLIITID: viewingMember.SLIITID
                    })}
                    size={200}
                    level="H"
                    includeMargin={true}
                  />
                </div>
                <h4 className="text-lg font-semibold text-white">{viewingMember.name}</h4>
                <p className="text-gray-400">{viewingMember.SLIITID}</p>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={closeModals}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-md text-white transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Member Profile Modal */}
        {isModalOpen && viewingMember && (
          <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-700 shadow-2xl shadow-black/50">
              {/* Modal Header */}
              <div className="p-6 border-b border-gray-700 flex justify-between items-center">
                <h3 className="text-xl font-semibold text-white">Member Profile</h3>
                <button 
                  onClick={closeModals}
                  className="text-gray-400 hover:text-white"
                >
                  <FaTimes className="text-xl" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Left Column - Profile */}
                  <div className="md:w-1/3 flex flex-col items-center">
                    <div className="w-32 h-32 rounded-full bg-gray-700 flex items-center justify-center text-4xl font-bold text-white mb-4">
                      {viewingMember.name ? viewingMember.name.charAt(0).toUpperCase() : '?'}
                    </div>
                    <h2 className="text-xl font-bold text-white text-center">{viewingMember.name}</h2>
                    <p className="text-gray-400 text-sm">{viewingMember.SLIITID}</p>
                    
                    {viewingMember.myLCI && (
                      <div className="mt-2 px-3 py-1 bg-blue-900/50 text-blue-300 text-xs rounded-full">
                        MYLCI Member
                      </div>
                    )}
                    
                    <div className="mt-4 w-full space-y-2">
                      <button 
                        className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center justify-center gap-2"
                        onClick={() => openQRCodeModal(viewingMember)}
                      >
                        <FaQrcode /> View QR Code
                      </button>
                    </div>
                  </div>

                  {/* Right Column - Details */}
                  <div className="md:w-2/3">
                    <h4 className="text-lg font-medium text-white mb-4">Personal Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gray-800/50 p-4 rounded-lg">
                        <h5 className="text-sm font-medium text-gray-400 mb-2 flex items-center">
                          <FaIdBadge className="mr-2" /> Registration No.
                        </h5>
                        <p className="text-white">{viewingMember.SLIITID || 'N/A'}</p>
                      </div>
                      
                      <div className="bg-gray-800/50 p-4 rounded-lg">
                        <h5 className="text-sm font-medium text-gray-400 mb-2 flex items-center">
                          <FaEnvelope className="mr-2" /> Email
                        </h5>
                        <p className="text-white">{viewingMember.email || 'N/A'}</p>
                      </div>
                      
                      <div className="bg-gray-800/50 p-4 rounded-lg">
                        <h5 className="text-sm font-medium text-gray-400 mb-2 flex items-center">
                          <FaPhone className="mr-2" /> Contact No.
                        </h5>
                        <p className="text-white">{viewingMember.contactNo || 'N/A'}</p>
                      </div>
                      
                      <div className="bg-gray-800/50 p-4 rounded-lg">
                        <h5 className="text-sm font-medium text-gray-400 mb-2 flex items-center">
                          <FaCalendarAlt className="mr-2" /> Date of Birth
                        </h5>
                        <p className="text-white">
                          {viewingMember.dob ? formatDate(viewingMember.dob) : 'N/A'}
                          {viewingMember.dob && ` (${calculateAge(viewingMember.dob)})`}
                        </p>
                      </div>
                      
                      <div className="bg-gray-800/50 p-4 rounded-lg">
                        <h5 className="text-sm font-medium text-gray-400 mb-2 flex items-center">
                          <FaUserGraduate className="mr-2" /> Faculty
                        </h5>
                        <p className="text-white">{viewingMember.faculty || 'N/A'}</p>
                      </div>
                      
                      <div className="bg-gray-800/50 p-4 rounded-lg">
                        <h5 className="text-sm font-medium text-gray-400 mb-2 flex items-center">
                          <FaMapMarkerAlt className="mr-2" /> Address
                        </h5>
                        <p className="text-white">{viewingMember.address || 'N/A'}</p>
                      </div>
                    </div>
                    
                    {/* Additional Information */}
                    <div className="mt-6">
                      <h4 className="text-lg font-medium text-white mb-4">Additional Information</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-gray-800/50 p-4 rounded-lg">
                          <h5 className="text-sm font-medium text-gray-400 mb-2">Position</h5>
                          <p className="text-white">{viewingMember.position || 'Member'}</p>
                        </div>
                        
                        <div className="bg-gray-800/50 p-4 rounded-lg">
                          <h5 className="text-sm font-medium text-gray-400 mb-2">Status</h5>
                          <div className="flex items-center">
                            <span className={`h-2 w-2 rounded-full mr-2 ${viewingMember.status === 'active' ? 'bg-green-500' : 'bg-red-500'}`}></span>
                            <span className="text-white capitalize">{viewingMember.status || 'inactive'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Modal Footer */}
              <div className="p-4 border-t border-gray-700 flex justify-end space-x-3">
                <button 
                  onClick={closeModals}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-md text-white transition-colors"
                >
                  Close
                </button>
                <button 
                  onClick={() => {
                    closeModals();
                    openQRCodeModal(viewingMember);
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-md text-white flex items-center gap-2 transition-colors"
                >
                  <FaQrcode /> Show QR Code
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
      
      {/* Notification Container */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {notifications.map(notification => {
          // Define styles based on notification type
          const typeStyles = {
            success: 'bg-green-500',
            error: 'bg-red-500',
            warning: 'bg-yellow-500',
            info: 'bg-blue-500'
          };
          
          // Get the appropriate style based on notification type
          const notificationStyle = typeStyles[notification.type] || typeStyles.info;
          
          return (
            <div 
              key={notification.id}
              className={`px-6 py-3 rounded-lg shadow-lg text-white transform transition-all duration-300 ease-in-out ${notificationStyle}`}
              style={{
                animation: 'slideIn 0.3s ease-out',
                opacity: 1,
                transition: 'opacity 0.3s ease-out',
                transform: 'translateX(0)',
                animationName: 'slideIn',
                animationDuration: '0.3s',
                animationTimingFunction: 'ease-out',
                animationFillMode: 'forwards'
              }}
              onAnimationEnd={(e) => {
                if (e.animationName === 'slideOut') {
                  removeNotification(notification.id);
                }
              }}
            >
              {notification.message}
            </div>
          );
        })}
      </div>
      
      {/* Add global styles */}
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideOut {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(100%); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

export default MemberDatabase;
